// src/components/Quotes/QuotesPage.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "../../api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaFileAlt, FaDownload } from "react-icons/fa";

import QuoteList from "./QuoteList";
import QuoteForm from "./QuoteForm";
import QuoteDetailView from "./QuoteDetailView";
import MissionCreateModal from "./MissionCreateModal";
import InvoiceCreateModal from "./InvoiceCreateModal";
import CommissionCelebration from "./CommissionCelebration";
import { useAuth } from "../Auth/AuthContext";
import { isOrgAdmin } from "../../utils/roles";

const PAGE_SIZE = 50;

export default function QuotesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = isOrgAdmin(user);
  const [syncingNextories, setSyncingNextories] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: "", status: "", month: "", provider: "" });

  // Modale création/édition de devis
  const [showForm, setShowForm] = useState(false);
  const [initialData, setInitialData] = useState(null);

  // Page détail (devis validés) — clic sur un devis validé
  const [detailQuoteId, setDetailQuoteId] = useState(null);

  // Modales Mission / Invoice
  const [showMissionModal, setShowMissionModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedQuoteFull, setSelectedQuoteFull] = useState(null);

  // Jamais null pour éviter "Cannot read properties of null"
  const [missionInitialData, setMissionInitialData] = useState({});
  const [invoiceInitialData, setInvoiceInitialData] = useState({});

  // Montant de commission à célébrer après validation (null = pas d'animation)
  const [commissionCelebration, setCommissionCelebration] = useState(null);

  // Validation en cours : la création facture + mission doit être confirmée,
  // sinon on remet le devis à son statut précédent. Ref = pas de re-render / pas de closure périmée.
  const validationRef = useRef(null);

  const parseMaybeJSON = useCallback((v, fallback) => {
    try {
      if (v == null) return fallback;
      if (typeof v === "string") return JSON.parse(v);
      return v;
    } catch {
      return fallback;
    }
  }, []);

  const fetchQuotes = useCallback(async (currentPage, currentFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: PAGE_SIZE });
      if (currentFilters.search) params.set("search", currentFilters.search);
      if (currentFilters.status) params.set("status", currentFilters.status);
      if (currentFilters.month)  params.set("month",  currentFilters.month);
      if (currentFilters.provider) params.set("provider", currentFilters.provider);
      const res = await api.get(`/quote?${params.toString()}`);
      const quotesData = res.data?.data || res.data || [];
      setQuotes(Array.isArray(quotesData) ? quotesData : []);
      if (res.data?.pagination) setPagination(res.data.pagination);
    } catch {
      toast.error(t("quote.fetchError", "Failed to load quotes"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchQuotes(page, filters);
  }, [fetchQuotes, page, filters]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const refresh = useCallback(() => {
    fetchQuotes(page, filters);
  }, [fetchQuotes, page, filters]);

  const handleSyncNextories = useCallback(async () => {
    setSyncingNextories(true);
    try {
      const res = await api.post("/quote/sync-nextories");
      const { imported, skipped } = res.data || {};
      if (imported > 0) {
        toast.success(t("quote.nextoriesImported", { count: imported, defaultValue: `${imported} nouveau(x) devis importé(s) depuis Nextories` }));
      } else {
        toast.info(t("quote.nextoriesNoNew", { defaultValue: `Aucun nouveau lead (${skipped ?? 0} déjà importés)` }));
      }
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("quote.nextoriesError", "Échec de l'import Nextories"));
    } finally {
      setSyncingNextories(false);
    }
  }, [t, refresh]);

  const quotesSorted = useMemo(() => {
    const arr = [...quotes];
    arr.sort((a, b) => {
      const da = new Date(a.date || 0).getTime();
      const db = new Date(b.date || 0).getTime();
      if (db !== da) return db - da;
      return (b.id || 0) - (a.id || 0);
    });
    return arr;
  }, [quotes]);

  const handleEditClick = (quote) => {
    setInitialData(quote);
    setShowForm(true);
  };

  const makeCopyName = (name) => `${name || "Quote"} (copy)`;

  // Normalise un item inventaire
  const normalizeItem = (it) => ({
    inventory_item_id:
      Number(it.inventory_item_id ?? it.itemId ?? it.id ?? it.inventoryId ?? 0) || 0,
    quantity: Math.max(1, Number(it.quantity) || 1),
  });

  const extractInventoryFromDetail = (full) => {
    let items = parseMaybeJSON(full.inventoryItems, null);
    if (!Array.isArray(items) || items.length === 0) {
      const inv = parseMaybeJSON(full.inventory, []);
      if (Array.isArray(inv) && inv.length) {
        items = inv.map(normalizeItem);
      }
    } else {
      items = items.map(normalizeItem);
    }
    return Array.isArray(items) ? items.filter((x) => x.inventory_item_id > 0) : [];
  };

  const fetchInventoryFallbacks = async (id) => {
    const candidates = [
      `/quote/${id}/inventory`,
      `/quote/${id}/items`,
      `/quote/${id}/inventory-items`,
    ];
    for (const ep of candidates) {
      try {
        const r = await api.get(ep);
        const raw = parseMaybeJSON(r.data, r.data);
        const arr = Array.isArray(raw) ? raw : parseMaybeJSON(raw?.items, []);
        if (Array.isArray(arr) && arr.length) {
          const norm = arr.map(normalizeItem).filter((x) => x.inventory_item_id > 0);
          if (norm.length) return norm;
        }
      } catch {
        /* ignore */
      }
    }
    return [];
  };

  // Copie de devis
  const handleCopy = async (quote) => {
    try {
      const detail = await api.get(`/quote/${quote.id}`);
      const full = detail.data || {};

      let inventoryItems = extractInventoryFromDetail(full);
      if (!inventoryItems.length) inventoryItems = await fetchInventoryFallbacks(quote.id);

      const payload = {
        client_name: makeCopyName(full.client_name),
        client_address: full.client_address,
        client_city: full.client_city,
        client_phone: full.client_phone,
        client_email: full.client_email,
        date: full.date,
        prestation_type: full.prestation_type,
        status: "pending",
        prestations: parseMaybeJSON(full.prestations, []),
        logistics: parseMaybeJSON(full.logistics, {}),
        calculationData: parseMaybeJSON(full.calculationData, {}),
        totalHT: full.totalHT,
        totalTVA: full.totalTVA,
        totalTTC: full.totalTTC,
        totalFullHT: full.totalFullHT,
        totalFullTTC: full.totalFullTTC,
        discount_percent: full.discount_percent ?? 0,
        ...(inventoryItems.length ? { inventoryItems } : {}),
      };

      const createRes = await api.post("/quote", payload);
      const newId = createRes?.data?.id;

      if (newId && inventoryItems.length) {
        const attachCandidates = [
          { method: "post", url: `/quote/${newId}/inventory`, body: { items: inventoryItems } },
          { method: "post", url: `/quote/${newId}/inventory-items`, body: { items: inventoryItems } },
          { method: "put", url: `/quote/${newId}/inventory`, body: inventoryItems },
        ];
        for (const c of attachCandidates) {
          try {
            await api[c.method](c.url, c.body);
            break;
          } catch {
            /* ignore */
          }
        }
      }

      toast.success(
        inventoryItems.length
          ? `Devis copié (${inventoryItems.length} objet${inventoryItems.length > 1 ? "s" : ""} d'inventaire)`
          : "Devis copié"
      );
      refresh();
    } catch (e) {
      console.error(e);
      toast.error("Échec de la copie du devis");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("quote.delete", "Delete") + " ?")) return;
    try {
      await api.delete(`/quote/${id}`);
      toast.success(t("quote.deleteSuccess", "Devis supprimé"));
      refresh();
    } catch {
      toast.error(t("quote.deleteFail", "Échec de la suppression du devis."));
    }
  };

  // Validation confirmée seulement quand facture + mission sont créées.
  const tryFinalizeValidation = () => {
    const v = validationRef.current;
    if (!v) return;
    if (v.needMission && !v.missionDone) return;
    if (v.needInvoice && !v.invoiceDone) return;
    validationRef.current = null;
    // Animation commission — uniquement pour le bénéficiaire (créateur/valideur crédité)
    const comm = v.commission;
    if (comm?.amount > 0 && (comm.createdBy == null || comm.createdBy === user?.id)) {
      setCommissionCelebration(comm.amount);
    }
    toast.success(t("quote.validated", "Devis validé — facture et mission créées."));
    refresh();
  };

  // Annulation : la création facture/mission n'a pas été confirmée → on remet le
  // statut précédent (le backend supprime facture/mission/RDV liés).
  const revertValidation = async () => {
    const v = validationRef.current;
    if (!v) return;
    validationRef.current = null;
    setShowMissionModal(false);
    setShowInvoiceModal(false);
    try {
      await api.put(`/quote/${v.quoteId}/status`, { status: v.prevStatus });
      setQuotes((prev) =>
        prev.map((q) => (q.id === v.quoteId ? { ...q, status: v.prevStatus } : q))
      );
      toast.info(t("quote.validationCanceled", "Validation annulée — devis remis à son statut précédent."));
    } catch (e) {
      toast.error(t("quote.validationCancelFail", "Échec de l'annulation de la validation."));
    }
    refresh();
  };

  const handleChangeStatus = async (quote, status) => {
    // Mise à jour optimiste — le badge change immédiatement
    setQuotes((prev) =>
      prev.map((q) => (q.id === quote.id ? { ...q, status } : q))
    );

    try {
      const { data: statusResult } = await api.put(`/quote/${quote.id}/status`, { status });

      // Pour la validation, le succès n'est confirmé qu'après création facture+mission.
      if (status !== "validated") {
        toast.success(
          `${t("quote.statusUpdated", "Statut mis à jour")} → ${t(`quote.statusOptions.${status}`)}`
        );
      }

      if (status === "validated") {
        const comm = statusResult?.commission;

        const { data: full } = await api.get(`/quote/${quote.id}`);
        setSelectedQuoteFull(full);

        // Contexte de validation : facture toujours requise, mission requise si pas déjà existante.
        validationRef.current = {
          quoteId: quote.id,
          prevStatus: quote.status,
          needInvoice: true,
          needMission: !statusResult.missionExists,
          invoiceDone: false,
          missionDone: false,
          commission: comm, // célébrée seulement à la finalisation
        };

        // totalHT = NET (remise déduite) ; totalFullHT = brut. Les lignes de prestation
        // sont brutes → InvoiceForm recalcule le total à partir des lignes. On ajoute
        // une ligne "Remise" négative pour que la facture reflète le net.
        const invPrestations = parseMaybeJSON(full.prestations, []);
        const discountHT = Number(
          ((Number(full.totalFullHT ?? full.totalHT ?? 0)) - Number(full.totalHT ?? 0)).toFixed(2)
        );
        if (discountHT > 0) {
          const pct = Number(full.discount_percent) || 0;
          invPrestations.push({
            description: `Remise commerciale (${pct % 1 === 0 ? pct : pct.toFixed(2)}%)`,
            prix: -discountHT,
            quantite: 1,
          });
        }
        setInvoiceInitialData({
          quote_id: full.id,
          client_name: full.client_name,
          client_address: full.client_address,
          client_city: full.client_city,
          client_phone: full.client_phone,
          client_email: full.client_email,
          date: (full.date || "").slice(0, 10),
          prestations: invPrestations,
          totalHT: full.totalHT,
          totalTVA: full.totalTVA,
          totalTTC: full.totalTTC,
        });
        setShowInvoiceModal(true);

        if (!statusResult.missionExists) {
          setMissionInitialData({
            // Défaut déménagement ; Delivery seulement si prestation "livraison".
            type: /livr/i.test(full.prestation_type || "") ? "Delivery" : "Moving",
            client: full.client_name,
            address: `${full.client_address || ""} ${full.client_city || ""}`.trim(),
            date: (full.date || "").slice(0, 10),
            priceHT: full.totalHT ?? full.totalFullHT ?? 0,
            calculationData: parseMaybeJSON(full.calculationData, undefined),
            // Logistique pour pré-remplir les CP départ/arrivée dans le popup mission.
            logistics: parseMaybeJSON(full.logistics, full.logistics || {}),
          });
          setShowMissionModal(true);
        }
      }

      refresh();
    } catch (e) {
      console.error(e);
      // Revert l'update optimiste en cas d'erreur
      setQuotes((prev) =>
        prev.map((q) => (q.id === quote.id ? { ...q, status: quote.status } : q))
      );
      const serverMsg = e?.response?.data?.error;
      toast.error(serverMsg || t("quote.changeStatusFail", "Échec de la mise à jour du statut."));
    }
  };

  const openCreate = () => {
    setInitialData({
      status: "pending",
      prestations: [],
    });
    setShowForm(true);
  };

  const handleSubmit = async (payload) => {
    try {
      if (initialData?.id) {
        await api.put(`/quote/${initialData.id}`, payload);
        toast.success(t("quote.updateSuccess", "Devis mis à jour"));
      } else {
        await api.post("/quote", payload);
        toast.success(t("quote.createSuccess", "Devis créé"));
      }
      setShowForm(false);
      setInitialData(null);
      refresh();
    } catch (e) {
      console.error(e);
      const serverMsg = e?.response?.data?.error;
      toast.error(serverMsg || t("quote.updateFail", "Échec de la mise à jour du devis."));
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <FaFileAlt className="text-lg" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-base-content">
              {t("quote.management", "Devis")}
            </h1>
            <p className="text-sm text-base-content/40 mt-0.5">
              {t("quote.subtitle", "Gestion et suivi des devis clients")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={handleSyncNextories}
              disabled={syncingNextories}
              className="px-4 py-2 rounded-xl bg-base-100 border border-base-300 hover:border-indigo-400/50 hover:shadow-sm text-sm font-semibold text-base-content flex items-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              title={t("quote.nextoriesSyncTitle", "Importer les nouveaux leads depuis Nextories")}
            >
              <FaDownload className={`text-xs ${syncingNextories ? "animate-pulse" : ""}`} />
              {syncingNextories ? t("quote.nextoriesSyncing", "Import…") : t("quote.nextoriesSync", "Importer Nextories")}
            </button>
          )}
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-xl bg-primary hover:bg-primary/90 text-primary-content text-sm font-semibold shadow-sm transition-colors"
          >
            + {t("quote.createTitle", "Nouveau devis")}
          </button>
        </div>
      </div>

      {/* Loading state — indicateur seul, sans démonter la liste (sinon l'input recherche perd le focus à chaque frappe) */}
      {loading && (
        <div className="text-center text-base-content/40 text-sm py-4">
          {t("dashboard.loading", "Chargement…")}
        </div>
      )}

      {/* Liste des devis — toujours montée pour conserver le focus de la recherche */}
      <QuoteList
        quotes={quotesSorted}
        setQuotes={setQuotes}
        handleEditClick={handleEditClick}
        handleDelete={handleDelete}
        handleChangeStatus={handleChangeStatus}
        onCopy={handleCopy}
        onViewDetail={setDetailQuoteId}
        filters={filters}
        onFilterChange={handleFilterChange}
        page={page}
        pageCount={pagination.pages || 1}
        total={pagination.total || 0}
        onPageChange={setPage}
      />

      {detailQuoteId && (
        <QuoteDetailView
          quoteId={detailQuoteId}
          onClose={() => setDetailQuoteId(null)}
          onChanged={refresh}
          onEdit={(q) => {
            setDetailQuoteId(null);
            handleEditClick(q);
          }}
        />
      )}

      {/* Modale création / édition */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-4 md:p-8 flex items-center justify-center"
          onClick={() => {
            setShowForm(false);
            setInitialData(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-[96vw] max-w-[1100px] bg-base-100 rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col border border-base-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="sticky top-0 bg-base-100/95 backdrop-blur border-b border-base-200 px-5 py-3.5 flex items-center justify-between">
              <h2 className="text-base font-semibold text-base-content">
                {initialData?.id
                  ? t("quote.edit", "Modifier le devis")
                  : t("quote.createTitle", "Nouveau devis")}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setInitialData(null);
                }}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-base-content/40 hover:text-base-content hover:bg-base-200 transition-colors text-lg leading-none"
                aria-label="Close"
                title={t("buttons.cancel", "Fermer")}
              >
                ✕
              </button>
            </div>

            <div className="overflow-y-auto p-5">
              <QuoteForm onSubmit={handleSubmit} initialData={initialData} />
            </div>
          </div>
        </div>
      )}

      {/* Modales Mission et Invoice */}
      <MissionCreateModal
        isOpen={showMissionModal}
        onClose={() => {
          const v = validationRef.current;
          if (v && !v.missionDone) { revertValidation(); }
          else setShowMissionModal(false);
        }}
        initialData={missionInitialData || {}}
        onSuccess={() => {
          if (validationRef.current) validationRef.current.missionDone = true;
          setShowMissionModal(false);
          tryFinalizeValidation();
          refresh();
        }}
        quoteId={selectedQuoteFull?.id}
      />

      <InvoiceCreateModal
        isOpen={showInvoiceModal}
        onClose={() => {
          const v = validationRef.current;
          if (v && !v.invoiceDone) { revertValidation(); }
          else setShowInvoiceModal(false);
        }}
        onSuccess={() => {
          if (validationRef.current) validationRef.current.invoiceDone = true;
          setShowInvoiceModal(false);
          tryFinalizeValidation();
        }}
        initialData={invoiceInitialData || {}}
      />

      {commissionCelebration != null && (
        <CommissionCelebration
          amount={commissionCelebration}
          onDone={() => setCommissionCelebration(null)}
        />
      )}
    </div>
  );
}
